/**
 * All sound effects are synthesized live with the Web Audio API — there are no
 * audio files to license, host, or preload. The AudioContext is created lazily
 * and resumed on the first user gesture (autoplay policy), then reused.
 */

let ctx: AudioContext | null = null;
let master: GainNode | null = null;

function ac(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.15;
    master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** Call from the first user gesture to unlock / resume audio. */
export function unlockAudio(): void {
  ac();
}

function tone(
  c: AudioContext,
  out: GainNode,
  opts: {
    type: OscillatorType;
    from: number;
    to?: number;
    dur: number;
    delay?: number;
    peak?: number;
    exp?: boolean;
  },
): void {
  const t0 = c.currentTime + (opts.delay ?? 0);
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = opts.type;
  osc.frequency.setValueAtTime(opts.from, t0);
  if (opts.to !== undefined) {
    if (opts.exp) osc.frequency.exponentialRampToValueAtTime(Math.max(opts.to, 1), t0 + opts.dur);
    else osc.frequency.linearRampToValueAtTime(opts.to, t0 + opts.dur);
  }
  const peak = opts.peak ?? 0.9;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.dur);
  osc.connect(g).connect(out);
  osc.start(t0);
  osc.stop(t0 + opts.dur + 0.02);
}

/** Short rising blip; pitch climbs with the combo count. */
export function eat(combo: number): void {
  const c = ac();
  if (!c || !master) return;
  const base = 440 + Math.min(Math.max(combo, 0), 10) * 55;
  tone(c, master, { type: "triangle", from: base, to: base * 1.7, dur: 0.14, exp: true, peak: 0.8 });
}

/** Low boom for the bomb. */
export function bomb(): void {
  const c = ac();
  if (!c || !master) return;
  tone(c, master, { type: "sine", from: 160, to: 40, dur: 0.5, exp: true, peak: 1.0 });
  tone(c, master, { type: "sawtooth", from: 90, to: 30, dur: 0.4, exp: true, peak: 0.5 });
}

/** ~300ms filtered-noise sizzle for the hot pepper burn. */
export function burn(): void {
  const c = ac();
  if (!c || !master) return;
  const dur = 0.3;
  const t0 = c.currentTime;
  const buffer = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buffer;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.setValueAtTime(1800, t0);
  bp.frequency.linearRampToValueAtTime(700, t0 + dur);
  bp.Q.value = 0.8;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.7, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(bp).connect(g).connect(master);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

/** Soft low plop when food is missed. */
export function miss(): void {
  const c = ac();
  if (!c || !master) return;
  tone(c, master, { type: "sine", from: 240, to: 150, dur: 0.16, exp: true, peak: 0.35 });
}

/** Countdown beep; `final` makes the last "1" higher/brighter. */
export function beep(final = false): void {
  const c = ac();
  if (!c || !master) return;
  tone(c, master, { type: "square", from: final ? 880 : 560, dur: final ? 0.22 : 0.14, peak: 0.6 });
}

/** Three-note rising arpeggio at round end. */
export function fanfare(): void {
  const c = ac();
  if (!c || !master) return;
  const notes = [523.25, 659.25, 783.99];
  notes.forEach((f, i) => {
    tone(c, master!, { type: "triangle", from: f, dur: 0.28, delay: i * 0.12, peak: 0.7 });
  });
}
